/**
 * Qualified-leads feed table for the Location Detail page.
 *
 * One row per qualification record. Click → navigate to qualified-lead detail
 * page. Loading state renders 5 skeleton rows so the layout doesn't jump.
 */

import { useMemo } from 'react'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { TestUploadButton } from './TestUploadButton'
import { FormCallUploadButton } from './FormCallUploadButton'
import { UploadStatusBadge } from './UploadStatusBadge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  formatDateTimeParts,
  getQualifierOutcomeColor,
  getQualifierPatternColor,
} from '@/lib/ghl-qualifier'
import { useContactGoogleAdsStatus, type QualifiedLeadFeedRow } from '@/hooks/useGHLQualifierDashboard'

interface Props {
  readonly data: readonly QualifiedLeadFeedRow[]
  readonly isLoading?: boolean
  readonly onRowClick: (id: string) => void
  /** When true, render a per-row "Test upload" button (direct mode + test flag). */
  readonly showTestButton?: boolean
  /** When true, render a per-row Google Ads upload status badge (direct mode). */
  readonly showUploadStatus?: boolean
}

export function QualifiedLeadsTable({
  data,
  isLoading,
  onRowClick,
  showTestButton,
  showUploadStatus,
}: Props) {
  // Memoize the contact-id array so React Query's queryKey identity stays
  // stable across re-renders when `data` hasn't changed.
  const contactIds = useMemo(() => data.map((r) => r.contact_id), [data])
  const uploadStatusQuery = useContactGoogleAdsStatus(
    contactIds,
    'qualified',
    !!showUploadStatus
  )
  const statusMap = uploadStatusQuery.data ?? {}

  const colSpan = 5 + (showUploadStatus ? 1 : 0) + (showTestButton ? 1 : 0)
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Contact</TableHead>
            <TableHead>Outcome</TableHead>
            <TableHead>Pattern</TableHead>
            <TableHead className="text-right">Tags</TableHead>
            <TableHead className="text-right">Created</TableHead>
            {showUploadStatus && <TableHead className="text-right">Upload</TableHead>}
            {showTestButton && <TableHead className="text-right">Test</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading
            ? Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={`skeleton-${i}`}>
                  <TableCell colSpan={colSpan}>
                    <Skeleton className="h-5 w-full" />
                  </TableCell>
                </TableRow>
              ))
            : data.map((row) => {
                const outcome = getQualifierOutcomeColor(row.status, row.qualification_result)
                const pattern = getQualifierPatternColor(row.classification_pattern)
                const tagCount = row.tags_applied?.length ?? 0
                const displayName =
                  row.contact_name && row.contact_name.trim().length > 0
                    ? row.contact_name
                    : row.contact_id
                const subline =
                  row.contact_email ?? row.contact_phone ?? row.contact_id

                return (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => onRowClick(row.id)}
                  >
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{displayName}</span>
                        <span className="text-xs text-muted-foreground">{subline}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <Badge className={`${outcome.bg} ${outcome.text} hover:${outcome.bg} w-fit`}>
                          {outcome.label}
                        </Badge>
                        {row.status === 'failed' && row.error_message ? (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className="text-[11px] text-muted-foreground cursor-help truncate max-w-[160px]">
                                  {row.error_message}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-sm">
                                {row.error_message}
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell>
                      {row.classification_pattern ? (
                        <Badge className={`${pattern.bg} ${pattern.text} hover:${pattern.bg}`}>
                          {pattern.label}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {tagCount > 0 ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help font-medium">{tagCount}</span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              {row.tags_applied?.join(', ')}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm">
                      {(() => {
                        const parts = formatDateTimeParts(row.created_at)
                        if (!parts) {
                          return <span className="text-muted-foreground">—</span>
                        }
                        return (
                          <div className="flex flex-col leading-tight">
                            <span>{parts.date}</span>
                            <span className="text-xs text-muted-foreground">{parts.time}</span>
                          </div>
                        )
                      })()}
                    </TableCell>
                    {showUploadStatus && (
                      <TableCell className="text-right">
                        <UploadStatusBadge row={statusMap[row.contact_id]} />
                      </TableCell>
                    )}
                    {showTestButton && (
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <TestUploadButton
                            contactId={row.contact_id}
                            locationId={row.location_id}
                            eventType="qualified"
                          />
                          <FormCallUploadButton
                            contactId={row.contact_id}
                            locationId={row.location_id}
                          />
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                )
              })}
          {!isLoading && data.length === 0 && (
            <TableRow>
              <TableCell colSpan={colSpan} className="py-12 text-center text-muted-foreground">
                No qualified leads found in this time range.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
